<template>
  <div class="vst-wrapper">
    <slot name="reportTitle"></slot>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="blue500"
      background-color="#c9daec"
      class="vst-progress-bar"
    ></v-progress-linear>
    <div ref="scrollContainer" class="vst-container scroll" @scroll="onScroll">
      <table class="vst-table" :style="widthsLocked ? { tableLayout: 'fixed' } : {}">
        <colgroup>
          <col v-for="(h, index) in headers" :key="h.value" :style="getColStyle(index)" />
        </colgroup>
        <slot
          name="header"
          :real-fixed-cols-count="realFixedColsCount"
          :fixed-cols-count="fixedColsCount"
          :real-sticky-offsets="realStickyOffsets"
          :sticky-offsets="stickyOffsets"
          :opened-groups="openedGroups"
          :opened-sub-groups="openedSubGroups"
          :headers-with-child-count="headersWithChildCount"
        >
          <vst-header
            :headers="headers"
            :loading="loading"
            :real-fixed-cols-count="realFixedColsCount"
            :fixed-cols-count="fixedColsCount"
            :real-sticky-offsets="realStickyOffsets"
            :sticky-offsets="stickyOffsets"
            :options="options"
            :opened-groups="openedGroups"
            :opened-sub-groups="openedSubGroups"
            :headers-with-child-count="headersWithChildCount"
            @update-options="onUpdateOptions"
          >
            <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
              <slot :name="slot" v-bind="scope"></slot>
            </template>
          </vst-header>
        </slot>
        <tbody>
          <tr>
            <td :colspan="headers.length" :style="{ height: topSpacer + 'px', padding: 0 }" />
          </tr>
          <vst-root-row
            v-for="item in visibleItems"
            :key="item.id"
            :item="item"
            :headers="headers"
            :real-fixed-cols-count="realFixedColsCount"
            :fixed-cols-count="fixedColsCount"
            :real-sticky-offsets="realStickyOffsets"
            :sticky-offsets="stickyOffsets"
            :expanded="expanded"
            :slot-level-title="slotLevelTitle"
            :item-class="itemClass"
            :value-mutator="valueMutator"
            @toggle="toggleRow"
            @toggle-deep="toggleRowDeep"
          >
            <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
              <slot :name="slot" v-bind="scope"></slot>
            </template>
          </vst-root-row>
          <tr>
            <td :colspan="headers.length" :style="{ height: bottomSpacer + 'px', padding: 0 }" />
          </tr>
        </tbody>
        <vst-footer
          :headers="headers"
          :total="total"
          :real-fixed-cols-count="realFixedColsCount"
          :fixed-cols-count="fixedColsCount"
          :real-sticky-offsets="realStickyOffsets"
          :sticky-offsets="stickyOffsets"
          :value-mutator="valueMutator"
        >
          <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
            <slot :name="slot" v-bind="scope"></slot>
          </template>
        </vst-footer>
      </table>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get'
import isString from 'lodash/isString'
import orderBy from 'lodash/orderBy'
import grColsWidth, { EXPAND_ARROW_WIDTH } from '@/constants/grColsWidth'
import VSTRootRow from './VSTRootRow.vue'
import VSTHeader from './VSTHeader.vue'
import VSTFooter from './VSTFooter.vue'

export default {
  name: 'UiVirtualScrollTable',
  components: {
    'vst-root-row': VSTRootRow,
    'vst-header': VSTHeader,
    'vst-footer': VSTFooter,
  },
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    openedGroups: {
      type: Array,
      default: () => [],
    },
    openedSubGroups: {
      type: Array,
      default: () => [],
    },
    openedThirdGroups: {
      type: Array,
      default: () => [],
    },
    openedFourthGroups: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    sortBy2: {
      type: Array,
      default: () => [],
    },
    sortDesc2: {
      type: Array,
      default: () => [],
    },
    sortBy3: {
      type: Array,
      default: () => [],
    },
    sortDesc3: {
      type: Array,
      default: () => [],
    },
    sortBy4: {
      type: Array,
      default: () => [],
    },
    sortDesc4: {
      type: Array,
      default: () => [],
    },
    sortBy5: {
      type: Array,
      default: () => [],
    },
    sortDesc5: {
      type: Array,
      default: () => [],
    },
    valueMutator: {
      type: Function,
      default: () => null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    itemClass: {
      type: Function,
      default: () => '',
    },
    fixedColsCount: {
      type: Number,
      default: 0,
    },
    headersWithChildCount: {
      type: Number,
      default: 0,
    },
    autoExpandFirstRow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rowHeight: 40,
      containerHeight: 0,
      scrollTop: 0,
      treeMap: {},
      columnWidths: {},
      widthsLocked: false,
      minTableHeight: 100,
      expanded: {},
      flatRows: [],
      displayItems: [],
      batchSize: 500,
      renderTimer: null,
      defaultExpandApplied: false,
    }
  },
  computed: {
    realFixedColsCount() {
      if (this.fixedColsCount === 0) return 0
      let count = this.fixedColsCount
      if (this.openedFourthGroups?.length > 0) {
        count = 5
      } else if (this.openedThirdGroups?.length > 0) {
        count = 4
      } else if (this.openedSubGroups?.length > 0) {
        count = 3
      } else if (this.openedGroups?.length > 0) {
        count = 2
      }
      if (count <= 0) return 0
      for (let i = count; i < this.headers.length; i++) {
        const header = this.headers[i]
        const headerClass = header.class || ''
        const hasMaxWidth = isString(headerClass)
          ? headerClass.includes('max-width-')
          : Array.isArray(headerClass)
          ? headerClass.some(c => isString(c) && c.includes('max-width-'))
          : false

        if (hasMaxWidth && !header.value?.startsWith('g.sub_ids.')) {
          count++
        } else {
          break
        }
      }
      return count
    },
    fixedStart() {
      if (this.openedFourthGroups?.length > 0) {
        return 3
      } else if (this.openedThirdGroups?.length > 0) {
        return 2
      } else if (this.openedSubGroups?.length > 0) {
        return 1
      }
      return 0
    },
    stickyOffsets() {
      const offsets = []
      let currentOffset = 0
      const fixedStart = this.fixedStart
      for (let i = 0; i < this.headers.length; i++) {
        if (i < fixedStart) {
          offsets.push(-1000)
        } else {
          offsets.push(currentOffset)
          currentOffset += this.columnWidths[i] || 0
        }
      }
      return offsets
    },
    realStickyOffsets() {
      const offsets = []
      let currentOffset = 0
      for (let i = 0; i < this.headers.length; i++) {
        offsets.push(currentOffset)
        currentOffset += this.columnWidths[i] || 0
      }
      return offsets
    },
    slotLevelTitle() {
      return {
        0: 'firstRow',
        1: 'secondRow',
        2: 'thirdRow',
        3: 'fourthRow',
        4: 'fifthRow',
      }
    },
    visibleCount() {
      return Math.ceil(this.containerHeight / this.rowHeight) + 10
    },
    startIndex() {
      return Math.floor(this.scrollTop / this.rowHeight)
    },
    endIndex() {
      return Math.min(this.startIndex + this.visibleCount, this.flatRows.length)
    },
    visibleItems() {
      return this.displayItems
    },
    topSpacer() {
      return this.startIndex * this.rowHeight
    },
    bottomSpacer() {
      return (this.flatRows.length - this.endIndex) * this.rowHeight
    },
    levelSortState() {
      return [
        { sortBy: this.sortBy2, sortDesc: this.sortDesc2 },
        { sortBy: this.sortBy3, sortDesc: this.sortDesc3 },
        { sortBy: this.sortBy4, sortDesc: this.sortDesc4 },
        { sortBy: this.sortBy5, sortDesc: this.sortDesc5 },
      ]
    },
  },
  watch: {
    items: {
      handler() {
        this.buildTree()
        this.updateTableHeight()
        this.lockColumnWidths()
      },
      immediate: true,
    },
    headers() {
      this.columnWidths = {}
      this.widthsLocked = false
      this.initColumnWidthsFromHeaders()
      this.lockColumnWidths()
    },
    options: {
      handler() {
        this.buildTree()
      },
      deep: true,
    },
    levelSortState: {
      handler() {
        this.buildTree()
      },
      deep: true,
    },
    flatRows() {
      this.updateTableHeight()
      this.updateDisplayItems()
    },
    containerHeight() {
      this.updateDisplayItems()
    },
    openedGroups() {
      this.lockColumnWidths()
    },
    openedSubGroups() {
      this.lockColumnWidths()
    },
    openedThirdGroups() {
      this.lockColumnWidths()
    },
    openedFourthGroups() {
      this.lockColumnWidths()
    },
  },
  created() {
    this.initColumnWidthsFromHeaders()
  },
  mounted() {
    this.containerHeight = this.$refs.scrollContainer.clientHeight
    this.updateTableHeight()
    this.buildTree()
    this.lockColumnWidths()
  },
  methods: {
    initColumnWidthsFromHeaders() {
      const newWidths = {}
      this.headers.forEach((header, index) => {
        let width = 0
        const headerClass = header.class || ''
        const classStr = Array.isArray(headerClass) ? headerClass.join(' ') : headerClass
        const classMatch = classStr.match(/max-width-(\d+)/)
        if (classMatch) {
          width = parseInt(classMatch[1], 10)
        } else if (header && header.value) {
          const key = header.value.replace('g.', '')
          if (grColsWidth[key]) {
            width = grColsWidth[key] + (header.value.startsWith('g.') ? EXPAND_ARROW_WIDTH : 0)
          }
        }
        if (width) {
          newWidths[index] = width
        }
      })
      if (Object.keys(newWidths).length) this.columnWidths = newWidths
    },
    lockColumnWidths() {
      this.$nextTick(() => {
        const table = this.$el.querySelector('.vst-table')
        table.style.tableLayout = 'auto'
        table.style.width = 'auto'

        const headerCells = this.$el.querySelectorAll('.vst-header-cell')

        if (headerCells.length === 0) {
          // Custom header: reconstruct column widths from thead structure
          const thead = this.$el.querySelector('thead')
          if (thead) {
            const widths = this.readCustomHeaderWidths(thead)
            const newWidths = { ...this.columnWidths }
            let hasChanges = false
            widths.forEach((w, i) => {
              if (w && w !== (newWidths[i] || 0)) {
                newWidths[i] = w
                hasChanges = true
              }
            })
            if (hasChanges) this.columnWidths = newWidths
            table.style.tableLayout = ''
            table.style.width = ''
            this.widthsLocked = widths.some(w => w > 0)
          }
          return
        }

        const newWidths = { ...this.columnWidths }
        let hasChanges = false
        headerCells.forEach((cell, index) => {
          let width = 0

          const match = cell.className.match(/max-width-(\d+)/)
          if (match) {
            width = parseInt(match[1], 10)
          } else {
            // header.minWidth — width needed by the widest value in the whole dataset, not only in rendered rows
            width = Math.max(Math.ceil(cell.getBoundingClientRect().width), this.headers[index]?.minWidth || 0)
          }

          if (width !== (newWidths[index] || 0)) {
            newWidths[index] = width
            hasChanges = true
          }
        })
        if (hasChanges) this.columnWidths = newWidths

        table.style.tableLayout = ''
        table.style.width = ''
        this.widthsLocked = true
      })
    },
    readCustomHeaderWidths(thead) {
      const rows = Array.from(thead.querySelectorAll('tr'))
      if (!rows.length) return []
      if (rows.length === 1) {
        return Array.from(rows[0].querySelectorAll('th')).map(th =>
          Math.ceil(th.getBoundingClientRect().width),
        )
      }
      // Multi-row header: map standalone cells (rowspan > 1) and group sub-columns
      const widths = []
      let col2Index = 0
      const row2Cells = Array.from(rows[rows.length - 1].querySelectorAll('th'))
      Array.from(rows[0].querySelectorAll('th')).forEach(cell => {
        const rowspan = parseInt(cell.getAttribute('rowspan') || '1')
        const colspan = parseInt(cell.getAttribute('colspan') || '1')
        if (rowspan > 1) {
          widths.push(Math.ceil(cell.getBoundingClientRect().width))
        } else {
          for (let i = 0; i < colspan; i++) {
            if (row2Cells[col2Index]) {
              widths.push(Math.ceil(row2Cells[col2Index].getBoundingClientRect().width))
              col2Index++
            }
          }
        }
      })
      return widths
    },
    updateTableHeight() {
      this.$nextTick(() => {
        const el = this.$refs.scrollContainer
        const headerEl = el.querySelector('.vst-header') || el.querySelector('thead')
        if (!el || !headerEl) return
        const viewportHeight = window.innerHeight - 10
        const headerHeight = headerEl.clientHeight + 11
        const footerHeight = 40
        const rowHeight = this.rowHeight
        const minHeight = this.minTableHeight
        const rowsCount = Math.max(this.flatRows.length, this.items.length || 0)
        const contentHeight = rowsCount * rowHeight + headerHeight + footerHeight
        const finalHeight = Math.min(Math.max(contentHeight, minHeight), viewportHeight)
        el.style.height = finalHeight + 'px'
        this.containerHeight = finalHeight - headerHeight - footerHeight
      })
    },
    rowClass(item) {
      const rowBgClasses = {
        1: 'multirow__second-row',
        2: 'multirow__third-row',
        3: 'multirow__fourth-row',
      }
      return item.level ? rowBgClasses[item.level] : ''
    },
    getValue(item, key) {
      const value = get(item, key)
      if (this.valueMutator) {
        return this.valueMutator({
          value,
          item,
          key,
        })
      }
      return value
    },
    onUpdateOptions(options) {
      this.$emit('update:options', options)
      this.$emit('update:sort-by')
    },
    onScroll(e) {
      const top = e.target.scrollTop
      if (this.renderTimer) cancelAnimationFrame(this.renderTimer)
      this.renderTimer = requestAnimationFrame(() => {
        this.scrollTop = top
        const start = this.startIndex
        const end = this.endIndex
        this.displayItems = this.flatRows.slice(start, end)
        this.renderTimer = null
      })
    },
    updateDisplayItems() {
      if (this.renderTimer) {
        cancelAnimationFrame(this.renderTimer)
      }
      this.renderTimer = requestAnimationFrame(() => {
        const start = this.startIndex
        const end = this.endIndex
        this.displayItems = this.flatRows.slice(start, end)
      })
    },
    recomputeFlatRows() {
      const rows = []
      const visit = row => {
        rows.push(row)
        if (row.hasChildren && this.expanded[row.id]) {
          row.children.forEach(child => visit(child))
        }
      }
      this.rootRows.forEach(row => visit(row))
      this.flatRows = rows
    },
    getColStyle(index) {
      const width = this.columnWidths[index]
      if (!width) return {}

      return {
        width: width + 'px',
        minWidth: width + 'px',
        maxWidth: width + 'px',
      }
    },
    getAllDescendants(parentId) {
      const result = []
      const map = this.treeMap
      function walk(id) {
        const row = map[id]
        if (row && row.children) {
          row.children.forEach(child => {
            result.push(child)
            walk(child.id)
          })
        }
      }
      walk(parentId)
      return result
    },
    collapseRow(row) {
      this.$delete(this.expanded, row.id)
      this.$emit(`openedGroups${row.level}`, row.id, false)
      const descendants = this.getAllDescendants(row.id)
      for (let i = 0; i < descendants.length; i++) {
        const child = descendants[i]
        this.$delete(this.expanded, child.id)
        this.$emit(`openedGroups${child.level}`, child.id, false)
      }
    },
    expandRow(row) {
      if (this.expanded[row.id]) return
      this.$set(this.expanded, row.id, true)
      this.$emit(`openedGroups${row.level}`, row.id, true)
    },
    toggleRow(row) {
      if (this.expanded[row.id]) this.collapseRow(row)
      else this.expandRow(row)
      this.recomputeFlatRows()
    },
    toggleRowDeep(row, isFullyExpanded) {
      if (isFullyExpanded) {
        this.collapseRow(row)
      } else {
        this.expandRow(row)
        this.getAllDescendants(row.id).forEach(child => {
          if (child.hasChildren) this.expandRow(child)
        })
      }
      this.recomputeFlatRows()
    },
    buildTree() {
      const map = {}
      const walk = (node, parentId = null, level = 0, index = 0) => {
        const row = {
          id: node.id || `${parentId ? parentId + '-' : ''}${level}-${index}`,
          level,
          parent: parentId,
          hasChildren: Boolean(node.sg?.length || node.tg?.length || node.fg?.length || node.ffg?.length),
          data: node,
          children: [],
        }
        map[row.id] = row
        const childrenGroups = [...(node.sg || []), ...(node.tg || []), ...(node.fg || []), ...(node.ffg || [])]
        const childrenRows = childrenGroups.map((child, idx) => walk(child, row.id, level + 1, idx))
        row.children = childrenRows.length ? this.sortedSubItems(childrenRows, level + 1) : childrenRows
        return row
      }

      const totalItems = this.items.length
      if (totalItems > this.batchSize) {
        this.rootRows = []
        let processed = 0
        const processBatch = () => {
          const end = Math.min(processed + this.batchSize, totalItems)
          const batch = this.items.slice(processed, end).map((item, index) => walk(item, null, 0, processed + index))
          this.rootRows.push(...batch)
          processed = end
          if (processed < totalItems) {
            this.renderTimer = requestAnimationFrame(processBatch)
          } else {
            this.treeMap = map
            this.recomputeFlatRows()
            this.applyDefaultExpand()
          }
        }
        processBatch()
      } else {
        this.rootRows = this.items.map((item, index) => walk(item, null, 0, index))
        this.treeMap = map
        this.recomputeFlatRows()
        this.applyDefaultExpand()
      }
    },
    applyDefaultExpand() {
      if (!this.autoExpandFirstRow || this.defaultExpandApplied || !this.rootRows.length) return
      this.defaultExpandApplied = true
      const firstRow = this.rootRows[0]
      this.toggleRow(firstRow)
      if (firstRow.children.length) this.toggleRow(firstRow.children[0])
    },
    sortedSubItems(arr, level) {
      if (level === 0) return arr
      const levelSort = this.levelSortState[level - 1] || { sortBy: [], sortDesc: [] }
      if (!levelSort.sortBy?.length) return arr
      const monthOrder = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
      ]
      const getMonthIndex = month => monthOrder.indexOf(month.toLowerCase())
      const getWeekStart = weekRange => this.$moment(weekRange.split(' - ')[0], 'DD.MM.YYYY')
      function sortGroupList(group, sortKey) {
        const value = get(group.data, sortKey)
        if (sortKey.includes('month')) {
          if (group.data?.g) group.data.g.monthIndex = value && isString(value) ? getMonthIndex(value) : -1
          return group
        } else if (sortKey.includes('week')) {
          return value && isString(value) ? getWeekStart(value) : value
        } else {
          return value && isString(value) ? value.toLowerCase() : value
        }
      }
      const sortKey = levelSort.sortBy[0] === 'g.month' ? 'g.month_title' : levelSort.sortBy[0]
      const orders = levelSort.sortDesc?.[0] ? 'desc' : 'asc'
      const sortFunc = group => sortGroupList(group, sortKey)
      if (sortKey === 'g.month_title') {
        orderBy(arr, sortFunc, orders)
        return orderBy(arr, [i => i.data?.g?.year, i => i.data?.g?.monthIndex], [orders, orders])
      } else {
        return orderBy(arr, sortFunc, orders)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.vst-wrapper {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 10px 0 rgba(188, 188, 188, 0.12), 0 4px 5px 0 rgba(188, 188, 188, 0.14),
    0 2px 4px -1px rgba(188, 188, 188, 0.2);
  .vst-progress-bar {
    border-radius: 16px 16px 0 0;
  }
}

.vst-container {
  overflow: auto;
  position: relative;
}

.vst-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.vst-header {
  tr.vst-header-preloader {
    position: sticky;
    top: 0;
  }
  th.vst-header-cell {
    text-wrap: nowrap;
    position: sticky;
    top: 0;
    background-color: #ffffff;
    z-index: 5;
    color: #aeaeae;
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    vertical-align: top;
    height: 40px;
    padding: 8px;
    border-bottom: 1px solid #eee;
    &.vst-header-preloader--loading {
      top: 4px;
    }
    &.vst-sticky {
      z-index: 6;
    }
  }
}
.vst-row {
  border-bottom: 1px solid #eee;
  td.vst-row-cell {
    background-color: #ffffff;
    color: #343434;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    height: 40px !important;
    padding: 0 8px;
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;
  }
}
.vst-footer td.vst-footer-cell {
  position: sticky;
  bottom: 0;
  background-color: #f8f8f8;
  color: #343434;
  font-weight: 400;
  z-index: 2;
  font-size: 12px;
  height: 40px;
  padding: 0 8px;
  border-bottom: 1px solid #eee;
  text-wrap: nowrap;
  &.vst-sticky {
    z-index: 3;
  }
}
.vst-sticky {
  position: sticky;
  z-index: 2;
  background: #fff;
}
.vst-table tbody {
  & > tr.multirow__second-row > td:nth-child(n + 2) {
    background-color: var(--v-violet50-base) !important;
    border-bottom: 1px solid var(--v-violet200-base) !important;
  }
  & > tr.multirow__third-row > td:nth-child(n + 3) {
    background-color: var(--v-teal50-base) !important;
    border-bottom: 1px solid var(--v-teal200-base) !important;
  }
  & > tr.multirow__fourth-row > td:nth-child(n + 4) {
    background-color: var(--v-orange50-base) !important;
    border-bottom: 1px solid var(--v-orange200-base) !important;
  }
}
.toggleUpDown {
  transition: transform 0.3s ease-in-out !important;
}
.toggleUpDown.rotate {
  transform: rotate(-180deg);
}
</style>
